import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ErrorMessage } from '../../../../../shared/models/error-message/error-message';
import { ErrorMessageApiService } from '../../../../services/api/error-message-api.service';

import { ControlValueAccessor, FormControl, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { AddComponent } from '../../../icons/add/add.component';
import { CreateErrorMessageModalComponent } from '../../../modals/error-message/create-error-message-modal/create-error-message-modal.component';
import { ErrorMessageQuery } from '../../../../../shared/models/error-messages/error-message-query';
import { EnabledByPolicyDirective } from '../../../../directives/enabled-by-policy.directive';
import { PaginationService } from '../../../../services/pagination.service';
import { Policy } from '../../../../../shared/models/info/policy';
import { initFlowbite } from 'flowbite';
import { ScrollableBase } from '../../../base/scrollable-base';
import { NgClass } from '@angular/common';
import { AgentActionDirective } from '../../../../directives/agent-action.directive';

@Component({
  selector: 'app-error-message-selection',
  imports: [
    FormsModule,
    AddComponent,
    CreateErrorMessageModalComponent,
    EnabledByPolicyDirective,
    NgClass,
    AgentActionDirective,
  ],
  templateUrl: './error-message-selection.component.html',
  styleUrl: './error-message-selection.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    PaginationService,
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ErrorMessageSelectionComponent),
      multi: true,
    },
  ],
})
export class ErrorMessageSelectionComponent
  extends ScrollableBase<ErrorMessageQuery>
  implements OnInit, OnChanges, AfterViewInit, OnDestroy, ControlValueAccessor
{
  @Input() public selectedErrorMessageId?: string;
  @Input() public dtcId?: string;
  @Input() public showCreateErrorMessage = false;
  @Input() public disabled = false;
  @Input() public unsavedChanges = false;

  @Output() public selectedErrorMessageTriggered = new EventEmitter<ErrorMessage | undefined>();

  @ViewChild('errorMessagesDropdownButton', { static: true })
  public dropdownButton!: ElementRef<HTMLButtonElement>;

  @ViewChild('errorMessageDropdownRadioHelper', { static: true })
  public dropdown!: ElementRef<HTMLDivElement>;
  public dummyErrorMessage = { headerOnTerminal: 'nothing', detailsOnTerminal: '', dtcIds: [] };
  public errorMessages: ErrorMessage[] = [this.dummyErrorMessage];
  public selectedErrorMessage?: ErrorMessage = this.errorMessages[0];
  public searchTerm = '';
  public policy = Policy;
  public selectControl?: FormControl;
  public readonly radioGroupName = `errmsg-radio-${Math.random().toString(36).slice(2)}`;

  private errorMessageApi = inject(ErrorMessageApiService);
  private cdr = inject(ChangeDetectorRef);
  private paginationService = inject(PaginationService<ErrorMessage>);

  private cachedErrorMessages: ErrorMessage[] = [];

  public ngOnInit(): void {
    window.addEventListener('resize', this.handleResize);
    this.fetchErrorMessages(true);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['dtcId']) {
      this.searchTerm = '';
      this.fetchErrorMessages(true);
    }
    if (changes['errorMessages'] || changes['selectedErrorMessage']) {
      this.cdr.detectChanges();
    } else if (changes['selectedErrorMessageId']) {
      this.initData();
    }
  }

  public override ngAfterViewInit(): void {
    this.setDropdownWidth();
    super.ngAfterViewInit();
    initFlowbite();
  }

  public ngOnDestroy(): void {
    window.removeEventListener('resize', this.handleResize);
  }

  public writeValue(value: string | null): void {
    this.selectedErrorMessageId = value ?? undefined;
    this.initData();
    this.cdr.detectChanges();
  }

  public registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.detectChanges();
  }

  public onErrorMessageSelect(selectedErrorMessage: ErrorMessage | undefined): void {
    this.selectedErrorMessage = selectedErrorMessage;
    this.selectedErrorMessageId = selectedErrorMessage?.id;
    this.onChange(selectedErrorMessage?.id ?? null);
    this.onTouched();
    this.selectedErrorMessageTriggered.emit(selectedErrorMessage);
    this.cdr.detectChanges();
  }

  public trackById(index: number, message: ErrorMessage): string {
    return message.id ?? '0';
  }

  public searchErrorMessages(keyEvent: KeyboardEvent): void {
    if (keyEvent.key === 'Escape') {
      this.errorMessages = this.cachedErrorMessages;
      this.searchTerm = '';
      return;
    } else if (keyEvent.key === 'Enter') {
      if (this.searchTerm === '') {
        this.errorMessages = this.cachedErrorMessages;
      } else {
        this.errorMessages = this.cachedErrorMessages;
        this.errorMessages = this.cachedErrorMessages.filter(
          (errorMessage) =>
            errorMessage.headerOnTerminal?.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
            errorMessage.detailsOnTerminal?.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
            errorMessage.code?.toString().includes(this.searchTerm)
        );
      }
    }
  }

  public onErrorMessageCreated(errorMessage: ErrorMessage): void {
    this.errorMessages.push(errorMessage);
    this.cachedErrorMessages = this.errorMessages;
    this.selectedErrorMessage = errorMessage;
    this.selectedErrorMessageId = errorMessage.id;
    this.onChange(errorMessage.id ?? null);
    this.onTouched();
    this.selectedErrorMessageTriggered.emit(errorMessage);
  }

  protected override onScroll(): void {
    const element = this.scrollContainer.nativeElement;
    if (element.scrollHeight - element.scrollTop <= element.clientHeight) {
      this.fetchErrorMessages(false);
    }
  }

  private initData(): void {
    if (this.selectedErrorMessageId) {
      this.selectedErrorMessage = this.errorMessages.find(
        (errorMessage) => errorMessage.id === this.selectedErrorMessageId
      );
    } else {
      this.selectedErrorMessage = this.errorMessages[0];
    }
  }

  private fetchErrorMessages(resetPagination: boolean): ErrorMessage[] {
    try {
      if (resetPagination) {
        this.paginationService.initialize(100);
      }

      this.paginationService.loadNextPage((page, pageSize) => {
        const query: ErrorMessageQuery = {
          page: page,
          pageSize: pageSize,
        };
        return this.errorMessageApi.getErrorMessages(query);
      });

      this.paginationService.items$.subscribe((errorMessages) => {
        this.errorMessages = [this.dummyErrorMessage, ...errorMessages];
        this.cachedErrorMessages = errorMessages;
        this.initData();
      });
      return this.errorMessages;
    } catch (error) {
      console.error('Error fetching error messages:', error);
      return [];
    }
  }

  private setDropdownWidth(): void {
    if (this.dropdownButton && this.dropdown) {
      const buttonWidth = this.dropdownButton.nativeElement.getBoundingClientRect().width;
      this.dropdown.nativeElement.style.width = `${buttonWidth}px`;
    }
  }

  private onChange: (value: string | null) => void = () => undefined;
  private onTouched: () => void = () => undefined;
  private readonly handleResize = (): void => this.setDropdownWidth();
}
